// Раздел «Люди»: реестр сотрудников на живых серверных данных.
//
// Скелет тот же, что у всех разделов (правило 1): колонка списка с переключателем
// представлений и отбором, колонка детали с вкладками. Новых страниц раздел не заводит.
//
// Представления приходят С СЕРВЕРА (/api/v1/employees/views) вместе со счётчиками, а не
// перечисляются в коде: типы оформления — данные администратора, и «Штат / Договор /
// Самозанятый», прибитые константой, разъехались бы с базой в первый же день. Счётчики тоже
// серверные: они посчитаны по той же области видимости, что и сам список.
//
// Всё состояние экрана лежит в адресе (правило 4): представление, поиск, отбор, выбранная
// строка, вкладка карточки и открытый мастер.

import { RiCompass3Line, RiUser3Line } from '@remixicon/react';
import { Link, useLocation, useParams } from 'react-router';

import { DetailPane } from '@/layout/DetailPane';
import { parseSearch } from '@/shared/routing/search';
import * as LinkButton from '@/shared/ui/link-button';
import { EmptyState } from '@/shared/ui/screen-state';

import { EmployeeCard } from './card/EmployeeCard';
import { СоставРеестра } from './components/СоставРеестра';
import { ViewSwitcher } from './components/ViewSwitcher';
import { useEmployeesQuery } from './queries';
import type { PeopleRoute } from './route';
import {
  DEFAULT_VIEW,
  FILTER_STATUS,
  hasNarrowing,
  parseView,
  peopleHref,
  setFilter,
} from './route';
import { EmployeesList, queryFromRoute } from './views/EmployeesList';

// Побочный эффект: раздел объявляет каркасу свои панели-мастера.
import './panels/register';

export function PeopleSection({ view: viewFromShell }: { view?: string }) {
  const params = useParams<{ view?: string; rowId?: string }>();
  const location = useLocation();

  const rawView = params.view ?? viewFromShell;
  const search = parseSearch(location.search);
  const view = parseView(rawView);

  if (view === null) {
    return (
      <main className='flex h-full flex-1 items-center justify-center bg-bg-weak-50'>
        <EmptyState
          icon={RiCompass3Line}
          title={`Представления «${rawView ?? ''}» в разделе «Люди» нет`}
          description='В разделе весь реестр и срезы по типу оформления. Похоже, ссылка устарела или в ней опечатка.'
          action={
            <LinkButton.Root variant='primary' size='medium' underline asChild>
              <Link to={peopleHref(DEFAULT_VIEW, { filters: {}, panels: [] })}>
                К реестру людей
              </Link>
            </LinkButton.Root>
          }
        />
      </main>
    );
  }

  const route: PeopleRoute = { view, rowId: params.rowId, search };

  return (
    <>
      <EmployeesList route={route} viewSwitcher={<ViewSwitcher route={route} />} />
      <SectionDetail route={route} />
    </>
  );
}

/**
 * Колонка детали: карточка выбранного человека либо состав реестра.
 *
 * Кнопки «завести сотрудника» здесь нет: она одна на раздел и стоит в шапке списка слева. Раньше
 * такая же жила и тут, под составом и под «Человек не выбран», и на рабочем столе на одном экране
 * стояли «Новый сотрудник» слева и «Завести нового сотрудника» справа: два входа в одно действие.
 */
function SectionDetail({ route }: { route: PeopleRoute }) {
  // Тот же ключ кэша, что у списка слева: второго запроса к серверу не будет.
  const список = useEmployeesQuery(queryFromRoute(route.view, route.search));
  if (route.rowId === undefined) {
    const люди = список.data?.items ?? [];
    const всего = список.data?.total;
    // Состав показывается, только когда приехали ВСЕ. Посчитанный по куску страницы, он был бы
    // назван итогом реестра — а это хуже пустой колонки: пустая не врёт.
    const целиком = всего !== undefined && люди.length === всего && люди.length > 0;
    if (целиком) {
      return (
        <DetailPane>
          <СоставРеестра
            люди={люди}
            всего={всего}
            ссылка={(статус) =>
              peopleHref(route.view, setFilter(route.search, FILTER_STATUS, статус))
            }
            // Состояние в сужение НЕ входит: состав делит на состояния всю выборку, и вкладка
            // «Резерв» не делает заголовок «Кто в выборке» из «Кто в реестре».
            сужено={hasNarrowing(route.view, route.search)}
          />
        </DetailPane>
      );
    }
    return (
      <DetailPane>
        <div className='flex h-full items-center justify-center'>
          <EmptyState
            icon={RiUser3Line}
            title='Человек не выбран'
            description='Выберите человека в списке слева: здесь откроется карточка с контактами и статусом, документами, назначениями с тарифом и историей статусов.'
          />
        </div>
      </DetailPane>
    );
  }
  return <EmployeeCard employeeId={route.rowId} route={route} />;
}
